/**
 * DiagnosticsTab — Lineage + Activity (+ field-scoped review items / rules /
 * samples) in collapsible accordion sections.
 *
 * Readiness rollout: the Health section was removed from here — the
 * structured findings UI now lives in the left Review pane and on the
 * Readiness chip in the toolbar, so surfacing them on the right rail too
 * was duplicate-surface bloat. Lineage is still hosted here; consumers
 * label this tab "Lineage" in the right rail.
 *
 * Used as the "Lineage" tab in MappingContextPanel (right panel, mapping mode).
 * Reuses existing canvas inspector-tab components.
 */
import React, { useMemo } from 'react';
import { useQuery } from 'react-query';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Box,
  Chip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  AccountTree as LineageIcon,
  // HealthIcon removed — Readiness rollout dropped the Health section
  History as ActivityIcon,
  RuleFolder as RulesIcon,
  TableChart as SamplesIcon,
  WarningAmber as ReviewIcon,
} from '@mui/icons-material';
import FieldLineageChain from '../FieldLineageChain';
// MappingHealthTab import removed — Health section is gone.
import { ActivityLogTab } from '../../canvas/inspector-tabs/ActivityLogTab';
import { useFeatureFlags } from '../../context/FeatureFlagContext';
import { FeatureName } from '../../types/featureNames';
import type { CanvasSelection, LineageChain } from '../../canvas/types';
import type { MappingHealthResponse, HealthIssue, MappingResponse } from '../../services/types';
import type { CanvasManualAdjustmentModel, CanvasPromptRunRecordModel } from '../../services/api';
import { apiService } from '../../services/api';
// Issue #656: workspace-mode field-scoped Inspector sections.
import {
  useReviewItems,
  type UnmappedTargetField,
} from '../../canvas/hooks/useReviewItems';
import { makeFieldKey } from '../../canvas/state/useFieldSelection';
import FieldReviewItems from '../../canvas/inspector/FieldReviewItems';
import FieldRules, {
  type ValidatorRule,
  type ValidatorOverrideEntry,
} from '../../canvas/inspector/FieldRules';
import FieldSampleValues from '../../canvas/inspector/FieldSampleValues';

// ── Types ────────────────────────────────────────────────────────────

export interface DiagnosticsTabProps {
  // Lineage
  lineageChains: LineageChain[];
  currentNode: string;
  currentNodeKind?: string;

  // Health
  healthData?: MappingHealthResponse | null;
  allHealthData?: MappingHealthResponse | null;
  healthLoading?: boolean;
  selectedField?: string;
  selectedTable?: string;
  onApplyFix?: (issue: HealthIssue, fixType: string) => void;

  // Activity
  manualAdjustments?: CanvasManualAdjustmentModel[];
  promptRuns?: CanvasPromptRunRecordModel[];
  selectedTargetField?: string | null;
  edgeFieldMap?: Record<string, string>;
  onUndo?: (id: string) => Promise<void>;
  onRedo?: (id: string) => Promise<void>;
  undoingId?: string | null;
  redoingId?: string | null;

  // Issue #656: data needed by the field-scoped Inspector sections
  // (Review items / Rules / Sample values). Without these the
  // workspace-mode right panel only shows Lineage + Health and the
  // plan's master/detail consolidation isn't visible.
  batchId?: string;
  mappingResponse?: MappingResponse | null;
  unmappedTargetFields?: UnmappedTargetField[];
  resolveDisabled?: boolean;
  onMaterializeSamples?: () => void;
  materializing?: boolean;
  onSelectInlineEdit?: (target_table: string, target_field: string) => void;
}

// ── Component ────────────────────────────────────────────────────────

const DiagnosticsTab: React.FC<DiagnosticsTabProps> = ({
  // Lineage
  lineageChains,
  currentNode,
  currentNodeKind,
  // Health
  healthData,
  allHealthData,
  healthLoading,
  selectedField,
  selectedTable,
  onApplyFix,
  // Activity
  manualAdjustments,
  promptRuns,
  selectedTargetField,
  edgeFieldMap,
  onUndo,
  onRedo,
  undoingId,
  redoingId,
  // Issue #656
  batchId,
  mappingResponse,
  unmappedTargetFields,
  resolveDisabled,
  onMaterializeSamples,
  materializing,
  onSelectInlineEdit,
}) => {
  // Issue #656: workspace-mode field-scoped Inspector data. Same
  // `useReviewItems` hook the Cell-1 Review pane uses; React Query
  // dedupes the underlying fetches so this doesn't double-fetch.
  const pendingDecisionsQuery = useQuery(
    ['pending-decisions', batchId],
    () => apiService.getPendingDecisions(batchId!),
    {
      enabled: Boolean(batchId) && Boolean(mappingResponse?.recipe_id),
      refetchOnWindowFocus: false,
    },
  );
  const reviewBundle = useReviewItems({
    mappingResponse: mappingResponse ?? null,
    healthIssues: healthData?.issues,
    pendingDecisions: pendingDecisionsQuery.data?.items,
    unmappedTargetFields,
  });

  // Field-scoped derivation. Mirrors what `CanvasMappingEditor.fieldScoped`
  // does — kept in sync because both consume the same `useReviewItems`
  // output and the same recipe shape.
  const fieldScoped = useMemo(() => {
    if (!selectedTable || !selectedField) {
      return {
        items: [] as ReturnType<typeof useReviewItems>['items'],
        rules: undefined as ValidatorRule[] | undefined,
        overrides: undefined as ValidatorOverrideEntry[] | undefined,
        isPrimaryKey: false,
        overriddenRuleNames: undefined as Set<string> | undefined,
        mapping: undefined,
      };
    }
    const items = reviewBundle.byField.get(makeFieldKey(selectedTable, selectedField)) ?? [];
    const meta = mappingResponse?.metadata as Record<string, any> | undefined;
    const tableSnapshot =
      meta?.target_schema_snapshot?.tables?.[selectedTable] ??
      meta?.schema_context?.target_schema_snapshot?.tables?.[selectedTable] ??
      mappingResponse?.transformation_plan?.target_schema_snapshot?.tables?.[selectedTable];
    const column = tableSnapshot?.columns?.find?.(
      (c: any) => c?.name === selectedField,
    );
    const rules = (column?.validator_rules ?? column?.rules ?? undefined) as
      | ValidatorRule[]
      | undefined;
    const overrides =
      mappingResponse?.validator_overrides?.[selectedTable]?.[selectedField] ??
      undefined;
    const overriddenRuleNames = overrides
      ? new Set(overrides.map((o: any) => o.rule))
      : undefined;
    const mapping =
      (mappingResponse?.mappings ?? []).find(
        (m) => m.target_table === selectedTable && m.target_field === selectedField,
      ) ?? undefined;
    return {
      items,
      rules,
      overrides,
      isPrimaryKey: Boolean(column?.is_primary_key ?? column?.primary_key),
      overriddenRuleNames,
      mapping,
    };
  }, [selectedTable, selectedField, reviewBundle.byField, mappingResponse]);

  const hasFieldSelection = Boolean(selectedTable && selectedField);
  const fieldReviewCount = fieldScoped.items.length;
  const theme = useTheme();
  const { hasFeature } = useFeatureFlags();
  const canViewLogs = hasFeature(FeatureName.SHOW_LOGS);

  const issueCount = useMemo(() => {
    if (!healthData?.issues) return 0;
    return healthData.issues.filter(
      (i) => i.severity === 'error' || i.severity === 'warning',
    ).length;
  }, [healthData]);

  const hasIssues = issueCount > 0;

  const activityCount = useMemo(
    () => (manualAdjustments?.length ?? 0) + (promptRuns?.length ?? 0),
    [manualAdjustments, promptRuns],
  );

  return (
    <Box sx={{ height: '100%', overflow: 'auto', p: 1 }}>
      {/* ── Lineage Section ── */}
      <Accordion defaultExpanded disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
          sx={{
            minHeight: 36,
            '& .MuiAccordionSummary-content': { my: 0.5, alignItems: 'center', gap: 1 },
          }}
        >
          <LineageIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" fontWeight={600}>
            Lineage
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 1, pt: 0 }}>
          {lineageChains.length > 0 ? (
            <FieldLineageChain
              currentNode={currentNode}
              currentNodeKind={currentNodeKind as any}
              chains={lineageChains}
            />
          ) : (
            <Typography variant="caption" color="text.secondary">
              Select a field to view its lineage chain.
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>

      {/* ── Issue #656: Field-scoped Review items ── */}
      {hasFieldSelection && batchId && (
        <Accordion
          defaultExpanded={fieldReviewCount > 0}
          disableGutters
          elevation={0}
          sx={{ '&:before': { display: 'none' } }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
            sx={{
              minHeight: 36,
              '& .MuiAccordionSummary-content': { my: 0.5, alignItems: 'center', gap: 1 },
            }}
          >
            <ReviewIcon
              sx={{
                fontSize: 16,
                color: fieldReviewCount > 0 ? 'warning.main' : 'text.secondary',
              }}
            />
            <Typography variant="body2" fontWeight={600}>
              Review items for this field
            </Typography>
            {fieldReviewCount > 0 && (
              <Chip
                label={fieldReviewCount}
                size="small"
                color="warning"
                variant="outlined"
                sx={{ height: 20, fontSize: 11 }}
              />
            )}
          </AccordionSummary>
          <AccordionDetails sx={{ p: 1, pt: 0 }}>
            <FieldReviewItems
              batchId={batchId}
              items={fieldScoped.items}
              resolveDisabled={resolveDisabled}
              overriddenRuleNames={fieldScoped.overriddenRuleNames}
              onSelectInlineEdit={onSelectInlineEdit}
            />
          </AccordionDetails>
        </Accordion>
      )}

      {/* ── Issue #656: Validator rules on this field ── */}
      {hasFieldSelection && (
        <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
            sx={{
              minHeight: 36,
              '& .MuiAccordionSummary-content': { my: 0.5, alignItems: 'center', gap: 1 },
            }}
          >
            <RulesIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" fontWeight={600}>
              Rules on this field
            </Typography>
            {fieldScoped.overrides && fieldScoped.overrides.length > 0 && (
              <Chip
                label={`${fieldScoped.overrides.length} overridden`}
                size="small"
                color="info"
                variant="outlined"
                sx={{ height: 20, fontSize: 11 }}
              />
            )}
          </AccordionSummary>
          <AccordionDetails sx={{ p: 1, pt: 0 }}>
            <FieldRules
              target_table={selectedTable as string}
              target_field={selectedField as string}
              rules={fieldScoped.rules}
              overrides={fieldScoped.overrides}
              isPrimaryKey={fieldScoped.isPrimaryKey}
            />
          </AccordionDetails>
        </Accordion>
      )}

      {/* ── Issue #656: Source + target sample values ── */}
      {hasFieldSelection && (
        <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
            sx={{
              minHeight: 36,
              '& .MuiAccordionSummary-content': { my: 0.5, alignItems: 'center', gap: 1 },
            }}
          >
            <SamplesIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" fontWeight={600}>
              Sample values
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 1, pt: 0 }}>
            <FieldSampleValues
              target_table={selectedTable as string}
              target_field={selectedField as string}
              mapping={fieldScoped.mapping ?? null}
              sampleOutput={mappingResponse?.sample_output}
              onMaterialize={onMaterializeSamples}
              materializing={materializing}
            />
          </AccordionDetails>
        </Accordion>
      )}

      {/* ── Health Section removed in Readiness rollout ──
          Findings are now the canonical channel for mapping issues and
          live in the left Review pane / Readiness chip — surfacing them
          on the right rail too was the duplicate-surface bloat the
          Readiness plan called out. The right rail keeps Lineage,
          Field rules, Sample values, and Activity. */}

      {/* ── Activity Section (gated by show_logs feature flag) ── */}
      {canViewLogs && (
        <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
            sx={{
              minHeight: 36,
              '& .MuiAccordionSummary-content': { my: 0.5, alignItems: 'center', gap: 1 },
            }}
          >
            <ActivityIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" fontWeight={600}>
              Activity
            </Typography>
            {activityCount > 0 && (
              <Chip
                label={activityCount}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: 11 }}
              />
            )}
          </AccordionSummary>
          <AccordionDetails sx={{ p: 1, pt: 0 }}>
            <ActivityLogTab
              manualAdjustments={manualAdjustments}
              promptRuns={promptRuns}
              selectedTargetField={selectedTargetField}
              edgeFieldMap={edgeFieldMap}
              onUndo={onUndo}
              onRedo={onRedo}
              undoingId={undoingId}
              redoingId={redoingId}
            />
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

export default DiagnosticsTab;
